import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Plus, MessageCircle, Users, TrendingUp, Clock, Pin, Lock, Search } from 'lucide-react';

const Community = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [newTopicForm, setNewTopicForm] = useState({
    title: '',
    content: '',
    category: 'general'
  });

  const handleCreateTopic = async () => {
    if (!newTopicForm.title || !newTopicForm.content) {
      alert('Please fill in title and content');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/community/topics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newTopicForm)
      });

      const data = await response.json();
      if (data.success) {
        alert('Topic created successfully!');
        setShowNewTopicModal(false);
        setNewTopicForm({ title: '', content: '', category: 'general' });
        window.location.reload();
      }
    } catch (error) {
      console.error('Error creating topic:', error);
      alert('Error creating topic');
    }
  };

  const categories = [
    { id: 'all', name: 'All Topics', count: 156, icon: <MessageCircle className="h-5 w-5" /> },
    { id: 'general', name: 'General Discussion', count: 45, icon: <MessageCircle className="h-5 w-5" /> },
    { id: 'best-practices', name: 'Best Practices', count: 32, icon: <Pin className="h-5 w-5" /> },
    { id: 'case-studies', name: 'Case Studies', count: 28, icon: <TrendingUp className="h-5 w-5" /> },
    { id: 'questions', name: 'Questions', count: 51, icon: <MessageCircle className="h-5 w-5" /> }
  ];

  const forumTopics = [
    { id: 1, title: 'Best practices for complex implant referrals', author: 'Dr. William Gray', avatar: 'WG', category: 'best-practices', replies: 24, views: 342, lastActivity: '2 hours ago', isPinned: true, isLocked: false },
    { id: 2, title: 'How do you handle urgent wisdom teeth cases?', author: 'Dr. Sarah Johnson', avatar: 'SJ', category: 'questions', replies: 18, views: 256, lastActivity: '5 hours ago', isPinned: false, isLocked: false },
    { id: 3, title: 'Patient education resources - What works best?', author: 'Dr. Michael Chen', avatar: 'MC', category: 'general', replies: 31, views: 489, lastActivity: '1 day ago', isPinned: false, isLocked: false },
    { id: 4, title: 'Case Study: Full arch restoration success story', author: 'Dr. Gerard Nikodem', avatar: 'GN', category: 'case-studies', replies: 42, views: 678, lastActivity: '2 days ago', isPinned: true, isLocked: false },
    { id: 5, title: 'Tips for improving referral response time', author: 'Dr. Elizabeth Fassler', avatar: 'EF', category: 'best-practices', replies: 15, views: 201, lastActivity: '3 days ago', isPinned: false, isLocked: false }
  ];

  const filteredTopics = selectedCategory === 'all' ? forumTopics : forumTopics.filter(t => t.category === selectedCategory);

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Users className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Community Forums
          </h1>
          <p className="text-gray-600">Connect with peers, share knowledge, and discuss best practices</p>
        </div>
        <Button 
          className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
          onClick={() => setShowNewTopicModal(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Topic
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Topics</p><p className="text-2xl font-bold text-[#0A1628]">156</p></div><MessageCircle className="h-8 w-8 text-[#D4AF37]" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Active Members</p><p className="text-2xl font-bold text-[#0A1628]">342</p></div><Users className="h-8 w-8 text-[#D4AF37]" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Replies</p><p className="text-2xl font-bold text-[#0A1628]">1,247</p></div><TrendingUp className="h-8 w-8 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Today's Activity</p><p className="text-2xl font-bold text-[#0A1628]">87</p></div><Clock className="h-8 w-8 text-[#D4AF37]" /></div></CardContent></Card>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader><CardTitle className="text-lg">Categories</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {categories.map((category) => (
                <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${selectedCategory === category.id ? 'bg-[#0A1628] text-[#D4AF37]' : 'hover:bg-gray-100 text-gray-700'}`}>
                  <div className="flex items-center space-x-2">{category.icon}<span className="text-sm font-medium">{category.name}</span></div>
                  <Badge variant="outline" className={selectedCategory === category.id ? 'border-[#D4AF37] text-[#D4AF37]' : ''}>{category.count}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {filteredTopics.map((topic) => (
            <Card key={topic.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-[#0A1628] text-[#D4AF37]">{topic.avatar}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-semibold text-[#0A1628]">{topic.title}</h3>
                          {topic.isPinned && <Pin className="h-4 w-4 text-[#D4AF37]" />}
                          {topic.isLocked && <Lock className="h-4 w-4 text-gray-400" />}
                        </div>
                        <p className="text-sm text-gray-600">by {topic.author}</p>
                      </div>
                      <Badge variant="outline">{topic.category}</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="flex space-x-6 text-sm text-gray-600">
                        <span className="flex items-center"><MessageCircle className="h-4 w-4 mr-1" />{topic.replies} replies</span>
                        <span className="flex items-center"><TrendingUp className="h-4 w-4 mr-1" />{topic.views} views</span>
                        <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{topic.lastActivity}</span>
                      </div>
                      <Button className="bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]" onClick={(e) => { e.stopPropagation(); navigate(`/community/${topic.id}`); }}>
                        View Topic
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {showNewTopicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Create New Topic</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input placeholder="Topic title" value={newTopicForm.title} onChange={(e) => setNewTopicForm({...newTopicForm, title: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select className="w-full px-3 py-2 border rounded-md" value={newTopicForm.category} onChange={(e) => setNewTopicForm({...newTopicForm, category: e.target.value})}>
                  <option value="general">General Discussion</option>
                  <option value="best-practices">Best Practices</option>
                  <option value="case-studies">Case Studies</option>
                  <option value="questions">Questions</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content *</label>
                <Textarea placeholder="Share your thoughts..." rows={8} value={newTopicForm.content} onChange={(e) => setNewTopicForm({...newTopicForm, content: e.target.value})} />
              </div>
              <div className="flex space-x-2">
                <Button className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]" onClick={handleCreateTopic}>
                  <Plus className="h-4 w-4 mr-2" />Create Topic
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setShowNewTopicModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;